import { useState } from 'react';
import { useLocalStorageValue } from '@react-hookz/web';
import { DateTime, T, Var, useGT } from 'gt-react';
import { EnvelopeSimpleIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/components/ui/toast';
import {
  useArCashReceipt,
  useArCashReceiptWorkItem,
  useDeleteCashReceipt,
  useUnlinkCashReceipt,
  useRematchCashReceipt,
  useSetCashReceiptRemittanceMissing,
} from '@/api/rpc/hooks/accounts-receivable/useArCashReceipts';
import {
  OriginReadOnlyBadge,
  isEditableCashReceiptOrigin,
} from '@/components/common/OriginReadOnlyBadge';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable';
import {
  EmailTabsPanel,
  type EmailTab,
} from '@/components/email-viewer/EmailTabsPanel';
import type {
  CashReceiptDetail,
  GetCashReceiptWorkItemResponse,
} from '@erp/rpc';
import {
  CashReceiptCustomerSection,
  CashReceiptInvoicedCustomersSection,
} from './cash-receipt-customer-section';
import { CashReceiptApplicationsSection } from './cash-receipt-applications-section';
import { CashReceiptBankSection } from './cash-receipt-bank-section';
import { CashReceiptWorkSection } from './cash-receipt-work-section';
import {
  CashReceiptHandPickSection,
  CashReceiptRemittanceLinksWithUnlink,
} from './cash-receipt-hand-pick-section';
import { CashReceiptActivitySection } from './cash-receipt-activity-section';
import { CashReceiptTaskSection } from './cash-receipt-task-section';
import { CashReceiptFormDialog } from './CashReceiptFormDialog';
import { formatAmount, formatDate } from './cashReceiptFormat';
import { CashReceiptLifecycleBadge } from './CashReceiptTaskCells';
import {
  CashReceiptSettlementBadge,
  CashReceiptSettlementSummary,
} from './CashReceiptSettlement';

type ReceiptRemittance = GetCashReceiptWorkItemResponse['remittances'][number];

/**
 * Tab label for a remittance with no source email, by how it arrived: the
 * document it was extracted from is all there is to show.
 */
function remittanceDocumentLabel(channel: ReceiptRemittance['channel']) {
  switch (channel) {
    case 'bank_memo':
      return (
        <T context="tab label, the bank memo a cash receipt's remittance was read from">
          Bank Memo
        </T>
      );
    case 'portal':
      return (
        <T context="tab label, a remittance downloaded from a customer portal">
          Portal Remittance
        </T>
      );
    default:
      return (
        <T context="tab label, remittance document on a cash receipt">
          Remittance Document
        </T>
      );
  }
}

/**
 * The sources worth showing beside a receipt: its own source email, plus the
 * remittance advices on the linked payment or links (or the candidates while
 * it is still unmatched). An advice shows its email; one that never came by
 * email (a portal download, a bank memo) shows its document instead. A
 * receipt folded from a remittance email can share that email with the
 * advice, so sources are deduped by id.
 */
function buildEmailTabs(
  receipt: CashReceiptDetail | null,
  workItem: GetCashReceiptWorkItemResponse | null,
): EmailTab[] {
  const tabs: EmailTab[] = [];
  if (receipt?.sourceEmail) {
    tabs.push({
      key: 'receipt',
      label: (
        <T context="tab label, the cash receipt's own source email">
          Cash Receipt
        </T>
      ),
      email: receipt.sourceEmail,
    });
  }

  const seenSourceIds = new Set(tabs.map((tab) => tab.email?.id));
  const remittances = [
    ...(workItem?.remittances ?? []),
    ...(workItem?.candidateRemittances ?? []),
  ].filter((remittance) => {
    const sourceId = remittance.sourceEmail?.id ?? remittance.documentId;
    if (sourceId == null || seenSourceIds.has(sourceId)) return false;
    seenSourceIds.add(sourceId);
    return true;
  });
  const emailCount = remittances.filter(
    (remittance) => remittance.sourceEmail,
  ).length;

  let emailIndex = 0;
  for (const remittance of remittances) {
    const { sourceEmail, documentId } = remittance;
    if (sourceEmail) {
      emailIndex += 1;
      tabs.push({
        key: `remittance-${sourceEmail.id}`,
        label:
          emailCount > 1 ? (
            <T context="tab label, nth remittance email on a cash receipt">
              Remittance <Var>{emailIndex}</Var>
            </T>
          ) : (
            <T context="tab label, remittance email on a cash receipt">
              Remittance
            </T>
          ),
        email: sourceEmail,
      });
    } else if (documentId) {
      tabs.push({
        key: `remittance-document-${documentId}`,
        label: remittanceDocumentLabel(remittance.channel),
        documentId,
      });
    }
  }

  return tabs;
}

function CashReceiptDetailHeader({
  receipt,
  isLoading,
  editable,
}: {
  receipt: CashReceiptDetail | null;
  isLoading: boolean;
  editable: boolean;
}) {
  // `pr-12` keeps the title clear of the host's close button, which sits
  // absolutely positioned in the pane's top-right corner.
  const wrapper = 'shrink-0 border-b px-4 py-3 pr-12';

  if (isLoading) {
    return (
      <div className={wrapper}>
        <h2 className="text-lg font-semibold">
          <T context="loading state for AR cash receipt detail">Loading...</T>
        </h2>
      </div>
    );
  }
  if (!receipt) {
    return (
      <div className={wrapper}>
        <h2 className="text-lg font-semibold">
          <T context="AR cash receipt detail when receipt id is unknown">
            Receipt not found
          </T>
        </h2>
      </div>
    );
  }
  return (
    <div className={wrapper}>
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <h2 className="text-2xl font-bold">
            {formatAmount(receipt.paymentAmount)}
          </h2>
          <CashReceiptLifecycleBadge lifecycle={receipt.lifecycle} />
          <CashReceiptSettlementBadge settlement={receipt.settlement} />
          {!editable && <OriginReadOnlyBadge />}
        </div>
        <p className="text-sm text-muted-foreground">
          {formatDate(receipt.paymentDate)}
        </p>
        <CashReceiptSettlementSummary receipt={receipt} />
      </div>
    </div>
  );
}

function CashReceiptSections({
  receipt,
  hideTaskCard,
}: {
  receipt: CashReceiptDetail;
  hideTaskCard: boolean;
}) {
  return (
    <>
      {!hideTaskCard && (
        <div className="py-4">
          <CashReceiptTaskSection receipt={receipt} />
        </div>
      )}
      <div className="py-4">
        <CashReceiptCustomerSection receipt={receipt} />
      </div>
      <div className="py-4">
        <CashReceiptInvoicedCustomersSection receipt={receipt} />
      </div>
      <div className="py-4">
        <CashReceiptApplicationsSection receipt={receipt} />
      </div>
      {receipt.remittanceLinks.length > 0 && (
        <div className="py-4">
          <CashReceiptRemittanceLinksWithUnlink
            receiptId={receipt.id}
            links={receipt.remittanceLinks}
          />
        </div>
      )}
      <div className="space-y-3 py-4">
        <CashReceiptWorkSection receipt={receipt} />
        <CashReceiptHandPickSection key={receipt.id} receipt={receipt} />
      </div>
      <div className="py-4">
        <CashReceiptBankSection receipt={receipt} />
      </div>
      <div className="py-4">
        <CashReceiptActivitySection receiptId={receipt.id} />
      </div>
    </>
  );
}

function CashReceiptDetailBody({
  receipt,
  emailTabs,
  isLoading,
  isError,
  hideTaskCard,
}: {
  receipt: CashReceiptDetail | null;
  emailTabs: EmailTab[];
  isLoading: boolean;
  isError: boolean;
  hideTaskCard: boolean;
}) {
  const { value: panelSize, set: setPanelSize } = useLocalStorageValue<number>(
    'ar-cash-receipt-details-panel-size',
    { defaultValue: 50, initializeWithValue: true },
  );

  if (isLoading) {
    return (
      <div className="p-4 text-sm text-muted-foreground">
        <T context="loading state for AR cash receipt detail body">
          Loading...
        </T>
      </div>
    );
  }
  if (isError) {
    return (
      <div className="p-4 text-sm text-destructive">
        <T context="error loading AR cash receipt detail">
          Failed to load receipt details. Please try again.
        </T>
      </div>
    );
  }
  if (!receipt) {
    return (
      <div className="p-4 text-sm text-muted-foreground">
        <T context="AR cash receipt detail when receipt is missing">
          Receipt not found
        </T>
      </div>
    );
  }
  if (emailTabs.length === 0) {
    return (
      <div className="divide-y px-4">
        <CashReceiptSections receipt={receipt} hideTaskCard={hideTaskCard} />
      </div>
    );
  }
  return (
    <ResizablePanelGroup direction="horizontal" className="h-full gap-2 px-3">
      <ResizablePanel
        defaultSize={panelSize}
        onResize={setPanelSize}
        minSize={25}
        className="flex flex-col pr-2"
      >
        <div className="flex-1 min-h-0 divide-y overflow-y-auto">
          <CashReceiptSections receipt={receipt} hideTaskCard={hideTaskCard} />
        </div>
      </ResizablePanel>
      <ResizableHandle />
      <ResizablePanel minSize={20}>
        <EmailTabsPanel tabs={emailTabs} />
      </ResizablePanel>
    </ResizablePanelGroup>
  );
}

/**
 * Mark that this cash should have come with a remittance email Gently never
 * recorded, or clear that mark. Offered only while no remittance is linked
 * (or once marked, so the mark can be cleared). The cash metrics dashboard
 * counts marked receipts as missed remittances.
 */
function RemittanceMissingAction({ receipt }: { receipt: CashReceiptDetail }) {
  const gt = useGT();
  const mutation = useSetCashReceiptRemittanceMissing();
  const marked = receipt.remittanceMissing;
  if (!marked && receipt.hasRemittance) return null;

  async function handleToggle() {
    try {
      await mutation.mutateAsync({ id: receipt.id, missing: !marked });
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : gt('Failed to update the missing remittance mark', {
              context: 'AR cash receipt, marking the remittance missing failed',
            }),
      );
    }
  }

  if (!marked) {
    return (
      <Button
        variant="outline"
        onClick={() => void handleToggle()}
        disabled={mutation.isPending}
        className="w-fit"
      >
        <EnvelopeSimpleIcon className="h-4 w-4" />
        <T context="AR cash receipt, flag that a remittance email should have come with this cash">
          Mark Remittance Missing
        </T>
      </Button>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-muted-foreground">
        {marked.markedByName ? (
          <T context="AR cash receipt, who flagged the remittance missing and when">
            Remittance marked missing by <Var>{marked.markedByName}</Var>,{' '}
            <DateTime options={{ dateStyle: 'medium' }}>
              {new Date(marked.markedAt)}
            </DateTime>
          </T>
        ) : (
          <T context="AR cash receipt, when the remittance was flagged missing">
            Remittance marked missing,{' '}
            <DateTime options={{ dateStyle: 'medium' }}>
              {new Date(marked.markedAt)}
            </DateTime>
          </T>
        )}
      </span>
      <Button
        variant="outline"
        size="sm"
        onClick={() => void handleToggle()}
        disabled={mutation.isPending}
        className="w-fit"
      >
        <T context="AR cash receipt, remove the missing remittance flag">
          Clear Mark
        </T>
      </Button>
    </div>
  );
}

/**
 * The receipt's action row, plus the two dialogs it opens. Owns its own
 * mutations so the parent view stays a layout shell.
 */
function CashReceiptDetailActions({
  receiptId,
  receipt,
  editable,
  onDeleted,
}: {
  receiptId: string;
  receipt: CashReceiptDetail | null;
  editable: boolean;
  onDeleted?: () => void;
}) {
  const gt = useGT();
  const deleteMutation = useDeleteCashReceipt();
  const unlinkMutation = useUnlinkCashReceipt();
  const rematchMutation = useRematchCashReceipt();
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  async function handleRematch() {
    try {
      const { receipt: updated } = await rematchMutation.mutateAsync({
        id: receiptId,
      });
      if (updated.status === 'matched') {
        toast.success(
          gt('Matched to a remittance.', {
            context: 'AR cash receipt re-match',
          }),
        );
      } else {
        toast.info(
          gt('No matching remittance found for this receipt.', {
            context: 'AR cash receipt re-match, no result',
          }),
        );
      }
    } catch (err) {
      toast.error(
        err instanceof Error
          ? `Failed to re-match: ${err.message}`
          : 'Failed to re-match cash receipt',
      );
    }
  }

  async function handleUnlink() {
    try {
      await unlinkMutation.mutateAsync({ id: receiptId });
      toast.success(
        gt('Unlinked from its remittances.', {
          context: 'AR cash receipt unlink',
        }),
      );
    } catch (err) {
      toast.error(
        err instanceof Error
          ? `Failed to unlink: ${err.message}`
          : 'Failed to unlink cash receipt',
      );
    }
  }

  async function handleConfirmDelete() {
    try {
      await deleteMutation.mutateAsync({ id: receiptId });
      setConfirmDeleteOpen(false);
      onDeleted?.();
    } catch (err) {
      // Surface the failure via toast and keep the dialog open so the
      // operator can retry. mutateAsync rethrows here even though the
      // mutation also captures `error` in its own state.
      toast.error(
        err instanceof Error
          ? `Failed to delete cash receipt: ${err.message}`
          : 'Failed to delete cash receipt',
      );
    }
  }

  return (
    <div className="shrink-0 flex flex-wrap items-center gap-2 border-t px-4 py-3">
      <Button
        variant="destructive"
        onClick={() => setConfirmDeleteOpen(true)}
        disabled={!receipt || deleteMutation.isPending}
        className="w-fit"
      >
        <T context="delete AR cash receipt button">Delete</T>
      </Button>
      {editable && (
        <Button
          variant="outline"
          onClick={() => setEditOpen(true)}
          className="w-fit"
        >
          <T context="edit AR cash receipt button">Edit</T>
        </Button>
      )}
      {receipt?.status === 'needs_review' && (
        <Button
          variant="outline"
          onClick={() => void handleRematch()}
          disabled={rematchMutation.isPending}
          className="w-fit"
        >
          <T context="manually run matching for AR cash receipt">Re-match</T>
        </Button>
      )}
      {receipt?.status === 'matched' && (
        <Button
          variant="outline"
          onClick={() => void handleUnlink()}
          disabled={unlinkMutation.isPending}
          className="w-fit"
        >
          <T context="break the AR cash receipt to remittance match">Unlink</T>
        </Button>
      )}
      {receipt && <RemittanceMissingAction receipt={receipt} />}

      {receipt && editable && (
        <CashReceiptFormDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          receipt={receipt}
        />
      )}

      <AlertDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <T context="confirm deleting an AR cash receipt">
                Delete this cash receipt?
              </T>
            </AlertDialogTitle>
            <AlertDialogDescription>
              <T context="explanation of AR cash receipt deletion side effects">
                This permanently removes the bank transaction and unlinks it
                from any matched payment. Use this for receipts that were
                created incorrectly. This action cannot be undone.
              </T>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              <T context="dialog cancel button">Cancel</T>
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleConfirmDelete();
              }}
              disabled={deleteMutation.isPending}
              className="bg-destructive hover:bg-destructive/90"
            >
              <T context="AR cash receipt destructive confirm">
                Delete Permanently
              </T>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

interface CashReceiptDetailViewProps {
  receiptId: string;
  /**
   * Called once the receipt is gone (deleted), so the host can drop it from
   * its selection instead of leaving the pane pointed at a missing row.
   */
  onDeleted?: () => void;
  /**
   * Hide the edit/unlink/delete footer — for hosts like the task page that
   * own their own actions.
   */
  hideActions?: boolean;
  /**
   * Hide the Task card — for the task page, which embeds this view and is
   * itself that task. Separate from `hideActions`: that one is about who
   * owns the receipt's edit/delete actions, this one about the task surface.
   */
  hideTaskCard?: boolean;
}

/**
 * A single cash receipt, rendered inline as a bounded box: title bar, its own
 * scrolling body (details beside the source/remittance emails), action footer.
 * The host owns the surrounding frame and the close affordance — this fills
 * whatever height it is given (`h-full`).
 */
export function CashReceiptDetailView({
  receiptId,
  onDeleted,
  hideActions = false,
  hideTaskCard = false,
}: CashReceiptDetailViewProps) {
  const { receipt, isLoading, isError } = useArCashReceipt(receiptId);
  // Same query the work section uses (deduped by key) — surfaced here so the
  // right-hand email panel can show the remittance emails beside the receipt's
  // own source email.
  const { workItem } = useArCashReceiptWorkItem(receiptId);
  const emailTabs = buildEmailTabs(receipt, workItem);
  const editable = receipt
    ? isEditableCashReceiptOrigin(receipt.originSystem)
    : false;

  return (
    <div className="flex h-full flex-col">
      <CashReceiptDetailHeader
        receipt={receipt}
        isLoading={isLoading}
        editable={editable}
      />
      <div className="flex-1 min-h-0 overflow-auto">
        <CashReceiptDetailBody
          receipt={receipt}
          emailTabs={emailTabs}
          isLoading={isLoading}
          isError={isError}
          hideTaskCard={hideTaskCard}
        />
      </div>
      {hideActions ? null : (
        <CashReceiptDetailActions
          receiptId={receiptId}
          receipt={receipt}
          editable={editable}
          onDeleted={onDeleted}
        />
      )}
    </div>
  );
}
